import type {
  DefinitionDetail,
  DefinitionReadiness,
  GeneratedDefinition,
  OrganisationAllowance,
} from "@keydials/api-client";
import { applyPreset, emptyDraft, RAG_PALETTE, toEditModel } from "@keydials/dial-workspace";
import type { NounForms } from "./draftSession";

// ---------------------------------------------------------------------------
// Opening a design in the builder, and creating one (KEY-560). The calls are passed in, so the rules can be tested without a
// server: readiness is the ownership check, the plan is checked before a draft is created (a draft costs a design slot the moment
// it exists), and every new draft is read back before it's edited, so the builder holds exactly what the server stored.
// ---------------------------------------------------------------------------

export interface BuilderLoadDeps {
  getDefinition(id: string): Promise<DefinitionDetail>;
  getReadiness(id: string): Promise<DefinitionReadiness>;
  getAllowance(): Promise<OrganisationAllowance>;
  createDefinition(model: GeneratedDefinition): Promise<{ id: string }>;
  copyTemplate(templateId: string): Promise<{ id: string }>;
  setNoun(id: string, noun: NounForms): Promise<void>;
}

export interface LoadedDraft {
  id: string;
  model: GeneratedDefinition;
  noun: NounForms | null;
  lifecycle: string;
  checkingInstanceId: string | null;
  readiness: DefinitionReadiness;
}

export type LoadResult = { kind: "loaded"; draft: LoadedDraft } | { kind: "notFound" } | { kind: "trialEnded" };

export type CreateResult =
  | { kind: "created"; draft: LoadedDraft }
  | { kind: "limit"; allowance: OrganisationAllowance }
  | { kind: "refused"; message: string }
  | { kind: "trialEnded" };

/** What the author chose at Start for a dial built from blank. */
export interface BlankChoices {
  name: string;
  purpose: string;
  kind: "Simple" | "Itemised";
  noun: NounForms | null;
}

const statusOf = (error: unknown): number | null => {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === "number" ? status : null;
};

function nounOf(detail: DefinitionDetail): NounForms | null {
  const singular = detail.segmentNounSingular?.trim();
  const plural = detail.segmentNounPlural?.trim();
  return singular && plural ? { singular, plural } : null;
}

/**
 * Opens a design. Readiness answers 404 for a dial the author didn't build (the definition itself is readable by anyone who can
 * see dials), so it doubles as the check that this design is theirs.
 */
export async function loadDraft(deps: BuilderLoadDeps, id: string): Promise<LoadResult> {
  try {
    const [detail, readiness] = await Promise.all([deps.getDefinition(id), deps.getReadiness(id)]);
    return {
      kind: "loaded",
      draft: {
        id,
        model: toEditModel(detail),
        noun: nounOf(detail),
        lifecycle: detail.lifecycle,
        checkingInstanceId: detail.checkingInstanceId ?? null,
        readiness,
      },
    };
  } catch (error) {
    const status = statusOf(error);
    if (status === 404 || status === 403) return { kind: "notFound" };
    if (status === 402) return { kind: "trialEnded" };
    throw error;
  }
}

/** The dial a blank start creates: the chosen kind in the recommended Traffic light colours, with its purpose. */
export function blankDraft(choices: BlankChoices): GeneratedDefinition {
  const draft = applyPreset(emptyDraft(choices.name, choices.kind, RAG_PALETTE), "traffic-light");
  const purpose = choices.purpose.trim();
  return purpose ? { ...draft, description: purpose } : draft;
}

/**
 * Creates a draft with `make`, names its word, and opens it. The plan is checked again right before the create, since it can
 * fill while the author is choosing (or while an AI draft is being made).
 */
export async function createDraft(
  deps: BuilderLoadDeps,
  make: () => Promise<{ id: string }>,
  noun: NounForms | null,
): Promise<CreateResult> {
  const allowance = await deps.getAllowance();
  if (!allowance.canCreateDefinition) return { kind: "limit", allowance };

  let id: string;
  try {
    ({ id } = await make());
  } catch (error) {
    const status = statusOf(error);
    if (status === 402) return { kind: "trialEnded" };
    if (status === 400) {
      // The plan can fill between the check and the create. Ask again rather than reading the sentence.
      const again = await deps.getAllowance().catch(() => null);
      if (again && !again.canCreateDefinition) return { kind: "limit", allowance: again };
      return { kind: "refused", message: error instanceof Error ? error.message : "" };
    }
    throw error;
  }

  if (noun) await deps.setNoun(id, noun);
  const loaded = await loadDraft(deps, id);
  if (loaded.kind === "loaded") return { kind: "created", draft: loaded.draft };
  if (loaded.kind === "trialEnded") return loaded;
  throw new Error("The new draft couldn't be opened.");
}

export const startBlank = (deps: BuilderLoadDeps, choices: BlankChoices): Promise<CreateResult> =>
  createDraft(deps, () => deps.createDefinition(blankDraft(choices)), choices.noun);

export const startFromTemplate = (deps: BuilderLoadDeps, templateId: string): Promise<CreateResult> =>
  createDraft(deps, () => deps.copyTemplate(templateId), null);
